// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { pluralize } from "@synd/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

export interface CartLinesListHeaderStyles {
    wrapper?: InjectableCss;
    productCountText?: TypographyPresentationProps;
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedCheckbox?: CheckboxPresentationProps;
    // XNG-Change
    editLink?: LinkPresentationProps;
    titleContainer?: InjectableCss;
    casesCount?: TypographyProps;
}

export const cartLinesListHeaderStyles: CartLinesListHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    productCountText: { weight: 600 },
    checkboxGroup: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
};

const CartLinesListHeader: FC<{
    productsCount: number;
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent, value: boolean) => void;
    extendedStyles?: CartLinesListHeaderStyles;
    // XNG-Change
    showEditCartLink?: boolean;
    totalQtyOrdered?: number;
}> = ({
    productsCount,
    onChangeIsCondensed,
    extendedStyles,
    isCondensed,
    hideCondensedSelector,
    showEditCartLink,
    totalQtyOrdered = 0,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartLinesListHeaderStyles, extendedStyles));

    const productsLabel = productsCount > 1 ? "Products" : "Product";

    // XNG-Change
    const cartPageUrl = useAppSelector(state => getPageLinkByPageType(state, "CartPage")?.url);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.productCountText} as="h3">
                    {}
                    {productsCount} {translate(productsLabel)}
                </Typography>
                {/* XNG-Changes */}
                {totalQtyOrdered > 0 && (
                    <Typography {...styles.casesCount}>
                        {pluralize(totalQtyOrdered, translate("Case"), { showNumber: true })}
                    </Typography>
                )}
                {showEditCartLink && (
                    <Link href={cartPageUrl} {...styles.editLink}>
                        {translate("Edit Cart")}
                    </Link>
                )}
            </StyledWrapper>
            {!hideCondensedSelector && (
                <CheckboxGroup {...styles.checkboxGroup}>
                    <Checkbox {...styles.condensedCheckbox} checked={isCondensed} onChange={onChangeIsCondensed}>
                        {translate("Condensed View")}
                        <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                    </Checkbox>
                </CheckboxGroup>
            )}
        </StyledWrapper>
    );
};

export default CartLinesListHeader;
